import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * DIVSN: "Passt dazu"-Empfehlung im Warenkorb.
 * Variantenwahl direkt in der Karte, Hinzufügen ohne Seitenwechsel.
 */

const SECTION_ID = 'cart-drawer-section';

/** @param {Element} card */
function readVariants(card) {
  try {
    return JSON.parse(card.querySelector('[data-divsn-upsell-variants]')?.textContent || '[]');
  } catch {
    return [];
  }
}

/** @param {HTMLElement} card */
function getSelects(card) {
  return /** @type {HTMLSelectElement[]} */ ([...card.querySelectorAll('[data-divsn-upsell-select]')]);
}

/** @param {HTMLElement} card */
function sync(card) {
  const btn = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-divsn-upsell-add]'));
  if (!btn) return;
  const selects = getSelects(card);
  const missing = selects.find((s) => !s.value);

  card.dataset.variantId = '';
  btn.disabled = false;

  if (missing) {
    btn.textContent = `${missing.getAttribute('aria-label')} wählen`;
    return;
  }

  const chosen = selects.map((s) => s.value);
  const variant = readVariants(card).find((v) => chosen.every((val, i) => v.o[i] === val));

  if (!variant || !variant.a) {
    btn.textContent = 'Nicht verfügbar';
    btn.disabled = true;
    return;
  }

  card.dataset.variantId = String(variant.id);
  btn.textContent = 'Hinzufügen';
}

/**
 * @param {HTMLElement} card
 * @param {HTMLButtonElement} btn
 * @param {number} variantId
 */
async function addToCart(card, btn, variantId) {
  if (card.dataset.busy) return;
  card.dataset.busy = '1';
  btn.disabled = true;
  btn.textContent = '…';

  const deferred = CartLinesUpdateEvent.createPromise();

  try {
    card.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ id: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const addUrl = globalThis.Theme?.routes?.cart_add_url ?? '/cart/add.js';
    const res = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: SECTION_ID,
        sections_url: window.location.pathname,
      }),
    });
    const added = await res.json();
    if (!res.ok || added.status) throw new Error(added.description || added.message || 'Hinzufügen fehlgeschlagen');

    const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    const cartJson = await cartRes.json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson),
      detail: {
        sections: added.sections,
        items: cartJson.items,
        itemCount: cartJson.item_count,
        source: 'divsn-upsell',
        didError: false,
      },
    });
  } catch (error) {
    console.warn('[divsn-upsell]', error);
    deferred.reject(error);
    btn.textContent = 'Nicht möglich';
    setTimeout(() => {
      if (card.isConnected) sync(card);
    }, 2000);
  } finally {
    delete card.dataset.busy;
  }
}

document.addEventListener('change', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const select = target?.closest('[data-divsn-upsell-select]');
  if (!select) return;
  select.classList.remove('is-missing');
  const card = /** @type {HTMLElement | null} */ (select.closest('[data-divsn-upsell]'));
  if (card) sync(card);
});

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const btn = /** @type {HTMLButtonElement | null} */ (target?.closest('[data-divsn-upsell-add]') ?? null);
  if (!btn) return;
  const card = /** @type {HTMLElement | null} */ (btn.closest('[data-divsn-upsell]'));
  if (!card) return;

  const missing = getSelects(card).find((s) => !s.value);
  if (missing) {
    missing.classList.add('is-missing');
    missing.focus();
    try {
      missing.showPicker?.();
    } catch {
      /* showPicker nicht überall erlaubt */
    }
    return;
  }

  sync(card);
  const variantId = Number(card.dataset.variantId);
  if (!variantId || btn.disabled) return;
  addToCart(card, btn, variantId);
});
